import { useMemo } from "react";
import { GridApi, RowNode } from "ag-grid-community";
import { generateMetricColId } from "helpers/metrics/column-formula/ag-grid-helper";
import { ColumnTypes } from "../../product-collection-grid-view/product-collection-grid-view-helper";
import { safeParseFloat } from "lib/number";
import { AllocationBag, AllocationBagType, sumAllocationBagByRowId } from "helpers/allocation-dimension";
import { FinancialTarget, FinancialTargetGroup } from "components/product-collection/footer/types";
import { formulaByKind } from "../helpers/formula-mapper";

/**
 * React hook to get actual values based on Target's filtered rows
 * @param targetGroup
 * @param targets
 * @param agGridApi It will be undefined in very edge case where main grid is not ready
 * @returns
 */
export const useTargetGroupValuesTotal = (
    targetGroup: FinancialTargetGroup,
    targets: FinancialTarget[],
    agGridApi?: GridApi
) => {
    const { targetsTotalMap, targetGroupTotal } = useMemo(() => {
        // TODO: Add back when we want to show actuals in Target list
        // const targetsTotalMap = agGridApi ? reduceTargetGroupTotal(targetGroup, targets, agGridApi) : {};
        const targetsTotalMap = {};
        const targetGroupTotal = Object.keys(targetsTotalMap).reduce((acc, targetId) => {
            return acc + targetsTotalMap[targetId];
        }, 0);

        return { targetGroupTotal, targetsTotalMap };
    }, []);

    return { totalBreakdown: targetsTotalMap, targetGroupTotal };
};

/**
 * React hook to get both
 * - Plan for specified target
 * - Actual values based on Target's filtered rows. This is achieved by using `useTargetGroupValuesTotal` hook
 * @param targetBag
 * @param targetGroup
 * @param targets
 * @param agGridApi
 * @returns
 */
export const useTargetGroupTotal = (
    targetBag: AllocationBag,
    targetGroup: FinancialTargetGroup,
    targets: FinancialTarget[],
    agGridApi: GridApi
) => {
    const { totalBreakdown, targetGroupTotal } = useTargetGroupValuesTotal(targetGroup, targets, agGridApi);
    const { targetsPlanTotalMap, targetGroupPlanTotal } = useMemo(() => {
        const targetsPlanTotalMap = reduceTargetGroupPlanTotal(targetBag, targetGroup, targets);
        const targetGroupPlanTotal = Object.keys(targetsPlanTotalMap).reduce((acc, targetId) => {
            return acc + targetsPlanTotalMap[targetId];
        }, 0);

        return { targetGroupPlanTotal, targetsPlanTotalMap };
    }, [targetBag, targetGroup, targets]);

    return {
        totalBreakdown,
        total: targetGroupTotal,
        planTotalBreakdown: targetsPlanTotalMap,
        planTotal: targetGroupPlanTotal,
    };
};

/**
 * Helper function to filter rows from main grid based on Target's filters.
 * This function is leveraging AgGrid API to get already-computed values from the main grid.
 * @param filters
 * @param rows
 * @param agGridApi
 * @returns
 */
export const filterRowsByTargetFilters = (filters: FinancialTarget["filters"], rows: RowNode[], agGridApi: GridApi) => {
    const [currentFilter, ...restFilters] = filters;

    if (!currentFilter) {
        return rows;
    }

    const filteredRows = rows.filter((rowNode) => {
        const match = agGridApi.getValue(currentFilter.field_name, rowNode) === currentFilter.value;
        return match;
    });

    return filterRowsByTargetFilters(restFilters, filteredRows, agGridApi);
};

/**
 * Helper function to get computed plan total and breakdown of target's plan total from Target bag.
 * @param targetBag
 * @param targetGroup
 * @param targets
 * @returns
 */
export const reduceTargetGroupPlanTotal = (
    targetBag: AllocationBag,
    targetGroup: FinancialTargetGroup,
    targets: FinancialTarget[]
) => {
    const reducedResult = {};

    targets.forEach((target) => {
        if (target.target_group_id === targetGroup.id) {
            const sumTotal = sumAllocationBagByRowId(AllocationBagType.target, targetBag, target.id);

            reducedResult[target.id] = sumTotal;
        }
    });

    return reducedResult;
};

/**
 * Helper function to get computed total and breakdown of target's total.
 * This function is leveraging AgGrid API to get already-computed values from the main grid.
 * TODO: Work on this when we need to show working total in Target Manage List
 * @param targetGroup
 * @param targets
 * @param agGridApi
 * @returns
 */
export const reduceTargetGroupTotal = (
    targetGroup: FinancialTargetGroup,
    targets: FinancialTarget[],
    agGridApi: GridApi
) => {
    const reducedResult = {};

    const rowNodes = [];
    agGridApi.forEachNode((node) => {
        rowNodes.push(node);
    });

    const metricKind = targetGroup.metric;
    const totalColumnId = generateMetricColId(ColumnTypes.AllDimensionTotal, metricKind);
    targets.forEach((target) => {
        if (target.target_group_id === targetGroup.id) {
            const targetRows = filterRowsByTargetFilters(target.filters, rowNodes, agGridApi);
            const rowValues = targetRows.map((targetRowNode) => {
                const value = agGridApi.getValue(totalColumnId, targetRowNode);
                const safeValue = safeParseFloat(value, 0);

                return safeValue;
            });

            const formulaFunction = formulaByKind[metricKind];
            const targetTotal = formulaFunction(rowValues);

            reducedResult[target.id] = safeParseFloat(targetTotal, 0);
        }
    });

    return reducedResult;
};
